import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { ChevronLeft, ChevronRight, Check, Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { getKnowledgeBaseResponse, upsertKnowledgeBaseResponse } from '@/services/data/knowledgeBase';
interface KnowledgeBaseWizardProps {
  userId: string;
  onComplete: () => void;
  onBack: () => void;
  variant?: 'card' | 'inline';
}

interface MultiInputItem {
  id: string;
  value: string;
}

// Helper function to convert string array to MultiInputItem array
const createMultiInputItems = (values: string[]): MultiInputItem[] => {
  return values.map(value => ({ id: crypto.randomUUID(), value }));
};

const questions = [
  {
    id: 'objectif',
    question: 'Quel est ton objectif ?',
    placeholder: 'Décris ton objectif principal...',
    type: 'textarea' as const,
  },
  {
    id: 'echec_si',
    question: 'Notre collaboration serait un échec total si...',
    placeholder: 'Décris ce qui constituerait un échec...',
    type: 'textarea' as const,
  },
  {
    id: 'domaines_parole',
    question: '2 à 4 domaines sur lesquels tu veux prendre la parole (piliers de contenu)',
    placeholder: 'Ex: 1. Expertise métier\n2. Innovation\n3. Conseil client\n4. Vision du secteur',
    type: 'textarea' as const,
  },
  {
    id: 'unique_different',
    question: "Qu'est-ce qui te rend unique / différent ?",
    placeholder: 'Décris ce qui te distingue...',
    type: 'textarea' as const,
  },
  {
    id: 'cibles',
    question: 'Qui sont tes cibles ?',
    placeholder: 'Ex: Dirigeants de PME, Entrepreneurs tech...',
    type: 'multi-input' as const,
    addButtonLabel: 'Ajouter une cible',
  },
  {
    id: 'cible_se_trompe',
    question: 'Sur quoi ta cible se trompe totalement ?',
    placeholder: 'Quelles idées reçues as-tu identifiées ?',
    type: 'textarea' as const,
  },
  {
    id: 'cible_inquiete',
    question: "Qu'est-ce qui inquiète ta cible ?",
    placeholder: 'Quelles sont leurs préoccupations principales ?',
    type: 'textarea' as const,
  },
  {
    id: 'cible_parle_de',
    question: "Qu'est-ce qui fait parler ta cible en ce moment ?",
    placeholder: "Quels sont les sujets d'actualité pour ta cible ?",
    type: 'textarea' as const,
  },
  {
    id: 'cible_reve',
    question: "Qu'est-ce qui fait rêver ta cible ?",
    placeholder: 'Quelles sont leurs aspirations ?',
    type: 'textarea' as const,
  },
  {
    id: 'opinions_tranchees',
    question: 'Tes opinions tranchées',
    placeholder: 'Quelles sont tes positions fortes ?',
    type: 'textarea' as const,
  },
  {
    id: 'ne_laisserai_jamais',
    question: '"En tant que ... je ne laisserai jamais ... faire ..."',
    placeholder: 'Complète cette phrase avec ta conviction...',
    type: 'textarea' as const,
  },
  {
    id: 'podcast_interview_article',
    question: 'Podcast / Interview / Article (liens)',
    placeholder: 'https://...',
    type: 'multi-input' as const,
    addButtonLabel: 'Ajouter un lien',
  },
];

export function KnowledgeBaseWizard({ userId, onComplete, onBack, variant = 'card' }: Readonly<KnowledgeBaseWizardProps>) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Ref for debounced auto-save
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Multi-input states
  const [multiInputData, setMultiInputData] = useState<Record<string, MultiInputItem[]>>({
    cibles: [{ id: crypto.randomUUID(), value: '' }],
    podcast_interview_article: [], // Empty by default - user must click to add
  });
  
  const [formData, setFormData] = useState<Record<string, any>>({
    objectif: '',
    echec_si: '',
    domaines_parole: '',
    unique_different: '',
    cible: '', // Keep for backwards compatibility
    cible_se_trompe: '',
    cible_inquiete: '',
    cible_parle_de: '',
    cible_reve: '',
    opinions_tranchees: '',
    ne_laisserai_jamais: '',
  });

  // Auto-save function for media links
  const autoSaveMediaLinks = useCallback(async (items: MultiInputItem[]) => {
    const validLinks = items.filter(item => item.value.trim()).map(item => item.value.trim());

    // Only save if there are valid links
    if (validLinks.length === 0) return;

    try {
      const currentDataResult = await getKnowledgeBaseResponse(userId);
      const currentData = currentDataResult.success && currentDataResult.data ? currentDataResult.data : {};

      const dataToSave = {
        ...currentData,
        media_links: validLinks
      };

      await upsertKnowledgeBaseResponse(userId, dataToSave);
    } catch (error) {
      console.error('Error auto-saving media links:', error);
    }
  }, [userId]);

  // Debounced auto-save trigger
  const triggerAutoSave = useCallback((items: MultiInputItem[]) => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSaveMediaLinks(items);
    }, 800); // 800ms debounce
  }, [autoSaveMediaLinks]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const loadData = async () => {
      const result = await getKnowledgeBaseResponse(userId);
      if (result.success && result.data) {
        setFormData(result.data);
        
        // Load media_links from the media_links JSONB column directly
        const mediaLinksData = result.data.media_links as string[] | null;
        if (mediaLinksData && Array.isArray(mediaLinksData) && mediaLinksData.length > 0) {
          const mediaLinkItems = createMultiInputItems(mediaLinksData);
          setMultiInputData(prev => ({
            ...prev,
            podcast_interview_article: mediaLinkItems
          }));
        }

        // Load cibles from the cible JSONB column directly
        const cibleData = result.data.cible as string[] | null;
        if (cibleData && Array.isArray(cibleData) && cibleData.length > 0) {
          const cibleItems = createMultiInputItems(cibleData);
          setMultiInputData(prev => ({
            ...prev,
            cibles: cibleItems
          }));
        }
      }
      setIsLoading(false);
    };
    loadData();
  }, [userId]);

  const currentQuestion = questions[currentStep];
  const currentValue = String(formData[currentQuestion.id] || '');

  const preserveExistingData = (dataToSave: Record<string, any>, currentData: Record<string, any> | null) => {
    if (!currentData) return dataToSave;

    const updated = { ...dataToSave };
    if (currentQuestion.id !== 'cibles' && currentData.cible) {
      updated.cible = currentData.cible;
    }
    if (currentQuestion.id !== 'podcast_interview_article' && currentData.media_links) {
      updated.media_links = currentData.media_links;
    }
    return updated;
  };

  const processMultiInputData = (dataToSave: Record<string, any>) => {
    if (currentQuestion.type !== 'multi-input') return dataToSave;

    const items = multiInputData[currentQuestion.id] || [];
    const validValues = items.filter(item => item.value.trim()).map(item => item.value.trim());

    if (currentQuestion.id === 'cibles') {
      return { ...dataToSave, cible: validValues };
    }
    if (currentQuestion.id === 'podcast_interview_article') {
      return { ...dataToSave, media_links: validValues };
    }
    return dataToSave;
  };

  const handleNext = async () => {
    setIsSaving(true);
    try {
      const currentDataResult = await getKnowledgeBaseResponse(userId);
      const currentData = currentDataResult.success && currentDataResult.data ? currentDataResult.data : null;

      let dataToSave: Record<string, any> = { ...formData };
      dataToSave = preserveExistingData(dataToSave, currentData);
      dataToSave = processMultiInputData(dataToSave);

      const result = await upsertKnowledgeBaseResponse(userId, dataToSave);

      if (result.success) {
        setFormData(prev => ({ ...prev, ...dataToSave }));

        if (currentStep < questions.length - 1) {
          setCurrentStep(currentStep + 1);
        } else {
          toast.success('Base de connaissance complétée !', {
            description: 'Merci d\'avoir renseigné ces informations, elles nous aideront à mieux préparer vos tournages.',
          });
          onComplete();
        }
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Error saving knowledge base:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleChange = (value: string) => {
    setFormData(prev => ({ ...prev, [currentQuestion.id]: value }));
  };

  const handleMultiInputChange = (questionId: string, id: string, value: string) => {
    setMultiInputData(prev => {
      const items = prev[questionId] || [];
      const updatedItems = items.map(item => item.id === id ? { ...item, value } : item);
      
      // Auto-save for podcast_interview_article (last step)
      if (questionId === 'podcast_interview_article') {
        triggerAutoSave(updatedItems);
      }
      
      return {
        ...prev,
        [questionId]: updatedItems
      };
    });
  };

  const handleAddMultiInput = (questionId: string) => {
    setMultiInputData(prev => {
      const updatedItems = [...(prev[questionId] || []), { id: crypto.randomUUID(), value: '' }];
      return {
        ...prev,
        [questionId]: updatedItems
      };
    });
  };

  const handleRemoveMultiInput = (questionId: string, id: string) => {
    setMultiInputData(prev => {
      const items = prev[questionId] || [];
      const updatedItems = items.filter(item => item.id !== id);
      
      // Auto-save for podcast_interview_article (last step)
      if (questionId === 'podcast_interview_article') {
        triggerAutoSave(updatedItems);
      }
      
      return { ...prev, [questionId]: updatedItems };
    });
  };

  const isInline = variant === 'inline';

  if (isLoading) {
    const loadingContent = (
      <div className={isInline ? "flex-1 flex items-center justify-center" : "fixed inset-0 bg-black flex items-center justify-center z-[60]"}>
        <Loader2 className="h-8 w-8 text-warning animate-spin" />
      </div>
    );

    return isInline || typeof document === 'undefined'
      ? loadingContent
      : createPortal(loadingContent, document.body);
  }

  const currentMultiInputItems = multiInputData[currentQuestion.id] || [{ id: crypto.randomUUID(), value: '' }];

  const renderQuestionInput = () => {
    if (currentQuestion.type === 'textarea') {
      return (
        <Textarea
          value={currentValue}
          onChange={(e) => handleChange(e.target.value)}
          className="bg-white/[0.12] border-white/[0.15] text-white placeholder:text-white/30 text-sm rounded-xl relative z-10 pointer-events-auto h-64 resize-none pt-4 pl-4 focus:ring-0 focus:border-white/[0.15]"
          placeholder={currentQuestion.placeholder}
          autoFocus
        />
      );
    }
    
    if (currentQuestion.type === 'multi-input') {
      return (
        <div className="flex-1 overflow-y-auto space-y-3">
          {currentMultiInputItems.map((item, index) => (
            <div key={item.id} className="flex items-center gap-2">
              <Input
                type={currentQuestion.id === 'podcast_interview_article' ? 'url' : 'text'}
                value={item.value}
                onChange={(e) => handleMultiInputChange(currentQuestion.id, item.id, e.target.value)}
                className="bg-white/[0.12] border-white/[0.15] text-white placeholder:text-white/30 text-sm rounded-xl relative z-10 pointer-events-auto flex-1 focus:ring-0 focus:border-white/[0.15] focus-visible:ring-0 focus-visible:ring-offset-0"
                placeholder={currentQuestion.placeholder}
                autoFocus={index === 0}
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleRemoveMultiInput(currentQuestion.id, item.id)}
                className="text-white/40 hover:text-white hover:bg-white/10 rounded-xl shrink-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          
          <Button
            variant="ghost"
            onClick={() => handleAddMultiInput(currentQuestion.id)}
            className="w-full glass border border-dashed border-white/20 text-white/60 hover:text-white hover:border-white/40 hover:bg-white/5 rounded-xl"
          >
            <Plus className="h-4 w-4 mr-2" />
            {currentQuestion.addButtonLabel || 'Ajouter'}
          </Button>
        </div>
      );
    }
    
    return null;
  };

  const renderButtonContent = () => {
    if (isSaving) {
      return (
        <>
          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
          Enregistrement...
        </>
      );
    }
    
    if (currentStep === questions.length - 1) {
      return (
        <>
          <Check className="h-5 w-5 mr-2" />
          Terminer
        </>
      );
    }
    
    return (
      <>
        Suivant
        <ChevronRight className="h-5 w-5 ml-2" />
      </>
    );
  };

  const content = (
    <div
      className={
        isInline
          ? "h-full flex flex-col pointer-events-auto"
          : "fixed inset-0 bg-black flex flex-col z-[60] pointer-events-auto pt-[env(safe-area-inset-top)]"
      }
    >

      {/* Header with close button */}
      <div className="px-6 py-5 shrink-0 flex items-center justify-end">
        <button
          type="button"
          onClick={onBack}
          className="p-1.5 -mr-1.5 rounded-full hover:bg-white/5 transition-colors text-white/40 hover:text-white/60"
          aria-label="Fermer"
        >
          <X className="h-5 w-5" />
        </button>
      </div>


      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 pointer-events-auto flex items-center">
        <div className="flex flex-col space-y-6 w-full">
          <div className="space-y-2">
            <p className="text-sm text-white/50">Question {currentStep + 1} sur {questions.length}</p>
            <h3 className="text-white text-lg font-medium leading-relaxed flex-shrink-0">
              {currentQuestion.question}
            </h3>
          </div>
          {renderQuestionInput()}

          {/* Buttons */}
          <div className="flex gap-3 mt-4">
            <button
              type="button"
              onClick={handlePreviousStep}
              disabled={currentStep === 0}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-4 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06] active:bg-white/[0.08] transition-all duration-200 text-sm font-medium text-white/60 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white/[0.03]"
            >
              <ChevronLeft className="h-5 w-5" />
              Précédent
            </button>
            <button
              onClick={handleNext}
              disabled={isSaving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-4 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06] transition-all duration-200 disabled:opacity-50 text-sm font-medium text-white/80"
            >
              {renderButtonContent()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return isInline || typeof document === 'undefined' ? content : createPortal(content, document.body);
}